import { useEffect } from "react";
import { useGoogleOneTapLogin } from "@react-oauth/google";
import { handleGoogleSignInAuthTokens } from "../../services/apiService";
import { useUser } from "./UserContext";

/** DOM id for the fixed One Tap container (z-index above navbar). */
const ONE_TAP_CONTAINER_ID = "google-one-tap-container";

interface GoogleOneTapProps {
  /** Called after a successful sign-in. */
  onLogin: () => void;
}

/**
 * Standalone Google One Tap component.
 *
 * Waits for the initial auth check (`authReady`) before activating.
 * If the user is already signed in (user.id !== 0) the prompt stays disabled.
 * Renders into a fixed container at z-index 1 000 000 so it's never blocked
 * by the navbar or any modal backdrop.
 */
const GoogleOneTap: React.FC<GoogleOneTapProps> = ({ onLogin }) => {
  const { user, authReady, setUser, setToken, setRefreshToken } = useUser();

  // Ensure the fixed container exists on <body> (created once)
  useEffect(() => {
    if (document.getElementById(ONE_TAP_CONTAINER_ID)) return;
    const el = document.createElement("div");
    el.id = ONE_TAP_CONTAINER_ID;
    Object.assign(el.style, {
      position: "fixed",
      top: "0",
      right: "0",
      zIndex: "1000000",
      width: "auto",
      height: "auto",
    });
    document.body.appendChild(el);
  }, []);

  const handleSuccess = async (credentialResponse: any) => {
    const result = await handleGoogleSignInAuthTokens(
      credentialResponse.credential
    );
    if (result) {
      setUser(result.user);
      setToken(result.tokens.access_token);
      setRefreshToken(result.tokens.refresh_token);
      onLogin();
    } else {
      console.error("One Tap login failed");
    }
  };

  // Only show the prompt once we KNOW the user is not authenticated.
  // Before authReady is true the hook stays disabled so it never fires
  // prematurely on page load.
  const shouldEnable = authReady && user.id === 0;

  useGoogleOneTapLogin({
    onSuccess: handleSuccess,
    onError: () => console.error("One Tap login failed"),
    promptMomentNotification: () => {},
    disabled: !shouldEnable,
    cancel_on_tap_outside: true,
    prompt_parent_id: ONE_TAP_CONTAINER_ID,
  });

  // This component renders nothing - it only runs the hook
  return null;
};

export default GoogleOneTap;
